import styled, { css } from 'styled-components';

import { HeroDescription } from '@/pages/Assignment/components';
import { useElementExpandable } from '@/hooks/useElementExpandable';
import { Button } from '@/ui/Button';
import { Skeleton } from '@/ui/Skeleton';
import { makeNamespacedComponent } from '@/ui/utils';
import { CustomEditorV2Readonly } from '@/component/customEditorV2';
import { toRichTextEditorValue } from '@/features/journey/admin/utils/richTextValue';

// The readonly editor ships page-level paddings and a 776px max-width meant for the
// course viewer; reset them so the content flows like plain hero description text.
const READONLY_EDITOR_STYLE = { fontSize: 14, padding: 0, maxWidth: '100%' } as const;

const DescriptionStyled = styled(HeroDescription)<{
  expanded: boolean;
  fullHeight?: number;
  collapseHeight?: number;
  canExpand?: boolean;
}>`
  overflow: hidden;
  position: relative;

  transition: max-height 0.3s ease-out;

  ${({ collapseHeight: customCollapseHeight = 280, canExpand }) => {
    const collapseHeight = customCollapseHeight;

    if (!canExpand) return false;

    return css`
      max-height: ${collapseHeight}px;

      &::after {
        content: '';
        position: absolute;
        z-index: 1;
        bottom: 0;
        left: 0;
        pointer-events: none;
        background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.9) 100%);
        width: 100%;
        height: 100px;
      }
    `;
  }}
`;

const Title = styled.div`
  ${(p) => p.theme.typography.title3};
  margin: 24px 0 16px 0;
`;

const ExpandDescriptionButton = styled(Button).attrs({ variant: 'ghost' })`
  margin-left: auto;
  /* The collapsed description's fade overlay (::after, z-index: 1) paints over the
     focus outline of the flush-adjacent button — keep the button above it. */
  position: relative;
  z-index: 2;
`;

const DESCRIPTION_COLLAPSE_THRESHOLD = 168;

type JourneyViewDescriptionProps = {
  description?: string;
  businessGoal?: string;
  spaceId: number;
  onShowMore: () => void;
};

const JourneyViewDescription = ({ description, businessGoal, spaceId, onShowMore }: JourneyViewDescriptionProps): JSX.Element => {
  const {
    ref: descriptionRef,
    expanded: descriptionExpanded,
    fullHeight: descriptionFullHeight,
    canExpand,
  } = useElementExpandable({
    collapseThreshold: DESCRIPTION_COLLAPSE_THRESHOLD,
  });

  return (
    <>
      <DescriptionStyled
        ref={descriptionRef}
        expanded={false}
        fullHeight={descriptionFullHeight}
        collapseHeight={DESCRIPTION_COLLAPSE_THRESHOLD}
        canExpand={canExpand}
      >
        {description && (
          <>
            <Title>Journey description</Title>
            <CustomEditorV2Readonly
              value={toRichTextEditorValue(description)}
              style={READONLY_EDITOR_STYLE}
              $fullSize
              minimized
              spaceId={spaceId}
            />
          </>
        )}
        {businessGoal && (
          <>
            <Title>Journey goals</Title>
            <CustomEditorV2Readonly
              value={toRichTextEditorValue(businessGoal)}
              style={READONLY_EDITOR_STYLE}
              $fullSize
              minimized
              spaceId={spaceId}
            />
          </>
        )}
      </DescriptionStyled>
      {canExpand && (
        <ExpandDescriptionButton icon={descriptionExpanded ? 'angle-up' : 'angle-down'} onClick={onShowMore}>
          Show more
        </ExpandDescriptionButton>
      )}
    </>
  );
};

const LoadingState = () => <Skeleton height={168} />;

const _JourneyViewDescription = makeNamespacedComponent(JourneyViewDescription, {
  LoadingState,
});

export { _JourneyViewDescription as JourneyViewDescription };
